import { useCallback } from 'react';

import { Menu as MuiMenu, MenuProps as MuiMenuProps, PopoverOrigin, SxProps, Theme } from '@mui/material';
import { VISUAL_REFRESH } from 'common/utils';
import { Menu as DsMenu } from 'components';

import { MenuItem, type MenuItemProps } from './components';
import { toDsProps } from './toDsProps';
import { MenuItemType } from './types';

export type MenuProps = Pick<MenuItemProps, 'onClose'> &
  Pick<
    MuiMenuProps,
    'sx' | 'transformOrigin' | 'anchorOrigin' | 'slotProps' | 'onTransitionExited' | 'disableScrollLock'
  > & {
    anchor: MuiMenuProps['anchorEl'];
    items: MenuItemType[];
    anchorOrigin?: PopoverOrigin;
    transformOrigin?: PopoverOrigin;
    sx?: SxProps<Theme>;
    activeIndex?: number;
  };

const defaultAnchorOrigin: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'center',
};

const defaultTransformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'center',
};

export const Menu = ({
  anchor,
  onClose,
  items,
  anchorOrigin = defaultAnchorOrigin,
  transformOrigin = defaultTransformOrigin,
  activeIndex,
  ...props
}: MenuProps) => {
  const isOpen = Boolean(anchor);

  /**
   * The visual-refresh gate lives HERE, not at the call sites — every menu in the app reaches this
   * component (most through `ButtonMenu`) and none of them mention the flag. `toDsProps` returns
   * `null` for anything DS 2.0 cannot express, which falls through to the MUI implementation below.
   *
   * Note it takes the WHOLE menu back rather than a single row: see the note in `toDsProps`.
   */
  const dsProps = VISUAL_REFRESH
    ? toDsProps({ anchor, items, activeIndex, anchorOrigin, transformOrigin, ...props })
    : null;

  /**
   * The DS component reports `open`; the legacy API only has "closed". Opening is driven by the call
   * site putting an element in `anchor`, so there is nothing to report back for it — which is why
   * this drops the `true` case rather than exposing an `onOpen`.
   */
  const handleOpenChange = useCallback((nextOpen: boolean) => !nextOpen && onClose?.(), [onClose]);

  if (dsProps) {
    return (
      <DsMenu
        {...dsProps}
        open={isOpen}
        onOpenChange={handleOpenChange}
        // The legacy prop is MUI's transition callback; Base UI's equivalent fires at the same point
        // in the lifecycle, which is what `ButtonMenu` uses to reset its own open state.
        onOpenChangeComplete={props.onTransitionExited ? () => props.onTransitionExited?.() : undefined}
      />
    );
  }

  return (
    <MuiMenu
      open={isOpen}
      anchorEl={anchor}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      onClick={(e) => {
        e.stopPropagation();
      }}
      disableRestoreFocus={true}
      onClose={onClose}
      {...props}
    >
      {items.map((item, index) => (
        <MenuItem key={index} isActive={activeIndex === index} item={item} onClose={onClose} parentMenuOpen={isOpen} />
      ))}
    </MuiMenu>
  );
};
